import React, { type FC, useEffect } from 'react'
import { Box, Grid, type SxProps, type Theme } from '@mui/material'

import { isEnumOf } from '../../../utils'
import { Capability, createIsAuthenticatedWithCapabilitiesSelector, selectExplorerError, Translation } from '../../../domain'
import { AbsoluteRouteOnlyPath, PermissionedLink } from '../routing'

import { useAppState, useAppUsecase } from '../State'
import { boldFormatter, createFormatter, FormattedTranslation } from '../Internationalization'
import { ErrorMessage } from '../ErrorMessage'
import { DetailsHeader } from '../common'

import { AvailableTopics } from './AvailableTopics'
import { MonitoredTopics } from './MonitoredTopics'
import { History } from './History'

const spacing = 2

const leftColumnStyle: SxProps<Theme> = { p: spacing, borderRight: '1px solid', borderColor: 'grey.300' }
const monitoredRowStyle: SxProps<Theme> = { p: spacing, borderBottom: '1px solid', borderColor: 'grey.300' }
const historyRowStyle: SxProps<Theme> = { p: spacing }
const missingReadTopicsPermissionStyle: SxProps<Theme> = { py: spacing }

const linkStyle = { color: 'info.main' }
const selectHasPermissionToReadTopicsMetadata = createIsAuthenticatedWithCapabilitiesSelector(Capability.TOPICS_SUBSCRIPTION_METADATA)

export const Explorer: FC = () => {
    const error = useAppState(selectExplorerError)

    const startExplorer = useAppUsecase('startExplorerSubscriptionUsecase')
    useEffect(() => startExplorer.invoke(), [startExplorer])

    if (error !== null) {
        return (
            <ErrorMessage
                data-testid="explorer-general-error"
                error={error}
                stack
                extras="section"
                customTitle={(e) => (isEnumOf(Translation, e.message) ? <FormattedTranslation id={e.message} /> : null)}
            />
        )
    }
    const hasPermissionToReadTopicsMetadata = useAppState(selectHasPermissionToReadTopicsMetadata)

    return (
        <Grid container>
            <Grid item xs={12}>
                <DetailsHeader
                    title={Translation.DATA_EXPLORER}
                    description={[Translation.DATA_EXPLORER_DOCUMENTATION_TITLE, Translation.DATA_EXPLORER_DOCUMENTATION_BODY]}
                />
            </Grid>
            {!hasPermissionToReadTopicsMetadata && (
                <Grid item xs={12}>
                    <Box sx={missingReadTopicsPermissionStyle} data-testid="missing-topics-read-permissions">
                        <FormattedTranslation
                            id={Translation.TOPICS_METADATA_PERMISSION_MISSING}
                            values={{
                                b: boldFormatter,
                                link: createFormatter(PermissionedLink, { path: AbsoluteRouteOnlyPath.SERVICES, sx: linkStyle }),
                            }}
                        />
                    </Box>
                </Grid>
            )}

            <Grid item xs={4} sx={leftColumnStyle}>
                <AvailableTopics />
            </Grid>
            <Grid item xs={8}>
                <Grid container direction="column">
                    <Grid item xs={12} sx={monitoredRowStyle}>
                        <MonitoredTopics />
                    </Grid>
                    <Grid item xs={12} sx={historyRowStyle}>
                        <History />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}
